import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import type { EChartsOption } from 'echarts';
import { useMemo } from 'react';

import { PALETTE, formatBucket } from 'entities/analytics/lib';
import { useVaccinations } from 'entities/analytics/model';
import { AnalyticsCard } from 'entities/analytics/ui/analytics-card';
import { EChart } from 'entities/analytics/ui/echart';

import { AnalyticsRangeParams, AnalyticsVaccinationsType } from 'shared/api';

const TYPE_OPTIONS: Array<{ value: AnalyticsVaccinationsType; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'people', label: 'People' },
  { value: 'pets', label: 'Pets' },
];

type Props = {
  params: AnalyticsRangeParams;
  type: AnalyticsVaccinationsType;
  onTypeChange: (t: AnalyticsVaccinationsType) => void;
};

export const VaccinationsCard = ({ params, type, onTypeChange }: Props) => {
  const granularity = params.granularity ?? 'month';
  const { data, isLoading, isFetching } = useVaccinations({ ...params, type });
  const series = useMemo(() => data?.data.series ?? [], [data]);
  const total = data?.data.total ?? 0;

  const option = useMemo<EChartsOption>(
    () => ({
      grid: { left: 8, right: 16, top: 12, bottom: 8, containLabel: true },
      tooltip: { trigger: 'axis' },
      xAxis: {
        type: 'category',
        data: series.map((p) => formatBucket(p.bucket, granularity)),
        axisLine: { lineStyle: { color: '#dfe5e3' } },
        axisLabel: { color: PALETTE.axis, fontSize: 10 },
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        splitLine: { lineStyle: { color: PALETTE.grid } },
        axisLabel: { color: PALETTE.axis, fontSize: 10 },
      },
      series: [
        {
          name: 'Vaccinations',
          type: 'bar',
          data: series.map((p) => p.count),
          itemStyle: { color: PALETTE.primary, borderRadius: [3, 3, 0, 0] },
        },
      ],
    }),
    [series, granularity],
  );

  return (
    <AnalyticsCard
      title="Vaccinations"
      subtitle="Unique actual vaccinations (distinct groupId)"
      totals={[{ label: 'Total vaccinations', value: total.toLocaleString() }]}
      rightAction={
        <ToggleButtonGroup
          size="small"
          exclusive
          value={type}
          onChange={(_, v) => v && onTypeChange(v as AnalyticsVaccinationsType)}
        >
          {TYPE_OPTIONS.map(({ value, label }) => (
            <ToggleButton key={value} value={value} sx={{ textTransform: 'none', px: 2 }}>
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      }
      isLoading={isLoading}
      isFetching={isFetching}
      isEmpty={!isLoading && series.length === 0}
      chartHeight={210}
      chart={<EChart option={option} height={210} />}
    />
  );
};
